/* Somewear — collection & search filtering with the Section Rendering API. */
(() => {
  const S = window.Somewear;

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.cache = new Map();
      this.debouncedUpdate = S.debounce(() => this.update(), 450);

      this.addEventListener('change', (event) => {
        if (!this.isFacetControl(event.target)) return;
        if (event.target.type === 'number') this.debouncedUpdate();
        else this.update();
      });
      this.addEventListener('input', (event) => {
        if (this.isFacetControl(event.target) && event.target.type === 'number') this.debouncedUpdate();
      });
      this.addEventListener('submit', (event) => {
        if (event.target.matches('[data-facets-form]')) {
          event.preventDefault();
          this.update();
        }
      });

      this.addEventListener('click', (event) => {
        const link = event.target.closest('[data-facet-link]');
        if (link) {
          event.preventDefault();
          const url = new URL(link.href, window.location.origin);
          const isPagination = link.closest('.pagination');
          this.render(url.searchParams.toString()).then(() => {
            if (isPagination) this.scrollIntoView({ behavior: S.reduceMotion() ? 'auto' : 'smooth' });
          });
          return;
        }
        if (event.target.closest('[data-facets-open]')) this.openDrawer(event.target.closest('[data-facets-open]'));
        if (event.target.closest('[data-facets-close]')) this.closeDrawer();
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.drawer && this.drawer.classList.contains('is-open')) this.closeDrawer();
      });

      window.addEventListener('popstate', () => this.render(window.location.search.slice(1), false));
    }

    get form() {
      return this.querySelector('[data-facets-form]');
    }

    get drawer() {
      return this.querySelector('[data-facets-drawer]');
    }

    isFacetControl(el) {
      return el && (el.form === this.form || el.closest('[data-facets-form]'));
    }

    params() {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') params.append(key, value);
      }
      return params.toString();
    }

    update() {
      this.render(this.params());
    }

    async render(query, pushState = true) {
      const url = `${window.location.pathname}?${query}`;
      const results = this.querySelector('[data-facets-results]');
      if (results) results.classList.add('is-loading');

      try {
        let html = this.cache.get(query);
        if (!html) {
          const separator = query ? '&' : '';
          const response = await fetch(`${window.location.pathname}?${query}${separator}section_id=${this.sectionId}`);
          html = await response.text();
          this.cache.set(query, html);
        }
        this.swap(S.parseHTML(html));
        if (pushState) window.history.pushState({ facets: true }, '', url);
      } catch (error) {
        window.location.href = url;
      } finally {
        const fresh = this.querySelector('[data-facets-results]');
        if (fresh) fresh.classList.remove('is-loading');
      }
    }

    swap(doc) {
      const incoming = doc.querySelector('facet-filters');
      if (!incoming) return;

      const activeId = document.activeElement && document.activeElement.id;
      const openDetails = new Set(
        [...this.querySelectorAll('.facet')].filter((d) => d.open).map((d) => d.querySelector('summary').textContent.trim())
      );

      const replace = (selector) => {
        const current = this.querySelector(selector);
        const next = incoming.querySelector(selector);
        if (current && next) current.innerHTML = next.innerHTML;
      };
      ['[data-facets-results]', '[data-count]', '.collection__filter-btn', '.facets__form', '.facets__footer'].forEach(replace);

      const currentSort = this.querySelector('[data-sort]');
      const nextSort = incoming.querySelector('[data-sort]');
      if (currentSort && nextSort) currentSort.value = nextSort.value;

      this.querySelectorAll('.facet').forEach((details) => {
        const key = details.querySelector('summary').textContent.trim();
        if (openDetails.has(key)) details.open = true;
      });

      if (activeId) {
        const next = document.getElementById(activeId);
        if (next) next.focus({ preventScroll: true });
      }

      S.observeReveals(this);
      const count = this.querySelector('[data-count]');
      if (count) S.announce(count.textContent.trim());
    }

    openDrawer(trigger) {
      if (!this.drawer) return;
      this.trigger = trigger;
      this.drawer.classList.add('is-open');
      document.body.classList.add('is-locked');
      const panel = this.drawer.querySelector('[role="dialog"]');
      window.requestAnimationFrame(() => panel.focus());
    }

    closeDrawer() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      if (this.trigger) this.trigger.focus();
    }
  }
  customElements.define('facet-filters', FacetFilters);
})();
